<script setup lang="ts">
import { Icon } from '@iconify/vue'
import { ref, onMounted, watch, computed } from 'vue'
import { useToast } from 'primevue/usetoast'
import { useRouter } from 'vue-router'
import { APIClient } from '@/onenetwork/core/onenetwork.api.instant'
import { PushCampaignAPI } from '../constants/push-campaign.api.uri.const'
import ConditionBuilder from '../components/ConditionBuilder.vue'
import { PushCampaignType as PushCampaignTypeEnum } from '../constants/push-campaign.enum'
import { validateCampaignForm, type ValidationError } from '../validation/campaign-form.schema'

type AudienceClause = { key: string; op: string; value: unknown }
type AudienceNode =
  | { type: 'GROUP_AND' | 'GROUP_OR'; child: AudienceNode[] }
  | { type: 'CONDITION'; child: AudienceClause[] }

interface Props {
  campaignId: number | null
}

const props = defineProps<Props>()
const toast = useToast()
const router = useRouter()

const linkTypeOptions = [
  { label: 'Liên kết IN-APP', value: 'INAPP' },
  { label: 'Liên kết ngoài', value: 'EXTERNAL' },
]

const errors = ref<ValidationError>({})

const hourOptions = Array.from({ length: 24 }, (_, i) => ({
  label: String(i).padStart(2, '0'),
  value: String(i).padStart(2, '0'),
}))
const minuteOptions = Array.from({ length: 60 }, (_, i) => ({
  label: String(i).padStart(2, '0'),
  value: String(i).padStart(2, '0'),
}))
const dayOptions = Array.from({ length: 31 }, (_, i) => ({
  label: String(i + 1).padStart(2, '0'),
  value: String(i + 1).padStart(2, '0'),
}))
const monthOptions = Array.from({ length: 12 }, (_, i) => ({
  label: String(i + 1).padStart(2, '0'),
  value: String(i + 1).padStart(2, '0'),
}))
const weekdayOptions = [
  { label: 'Thứ 2', value: '1' },
  { label: 'Thứ 3', value: '2' },
  { label: 'Thứ 4', value: '3' },
  { label: 'Thứ 5', value: '4' },
  { label: 'Thứ 6', value: '5' },
  { label: 'Thứ 7', value: '6' },
  { label: 'Chủ nhật', value: '7' },
]

// ---- Future scheduling: disable past dates/times ----
const today = new Date()
const todayStr = today.toISOString().split('T')[0] // 'YYYY-MM-DD'
const nowHour = today.getHours()
const nowMinute = today.getMinutes()

const minDate = computed(() => todayStr)

const isToday = computed(() => form.value.scheduled_date === todayStr)

const filteredFutureHourOptions = computed(() => {
  if (!isToday.value) return hourOptions
  return hourOptions.filter((h) => parseInt(h.value) > nowHour)
})

const filteredFutureMinuteOptions = computed(() => {
  if (!isToday.value) return minuteOptions
  const selectedHour = parseInt(form.value.scheduled_hour)
  if (selectedHour > nowHour) return minuteOptions
  return minuteOptions.filter((m) => parseInt(m.value) > nowMinute)
})

const form = ref({
  campaign_type: PushCampaignTypeEnum.NotificationLink,
  title: '',
  notification_title: '',
  notification_content: '',
  link_type: 'custom',
  link: '',
  reward_type: 'point' as 'point',
  reward_amount: '0',
  target_audience: 'all',
  audience_conditions: { type: 'GROUP_OR', child: [] } as AudienceNode,
  // Kế hoạch gửi
  type: 'now' as 'now' | 'schedule' | 'future',
  // Lập lịch để gửi
  scheduled_date: '',
  scheduled_hour: '08',
  scheduled_minute: '00',
  // Gửi định kỳ
  recurring_type: 'daily' as 'daily' | 'weekly' | 'monthly' | 'yearly',
  recurring_hour: '08',
  recurring_minute: '00',
  recurring_weekday: '2',
  recurring_day: '01',
  recurring_month: '01',
})

type CampaignSchedule = {
  hour?: number | null
  minute?: number | null
  date?: number | null
  month?: number | null
  weekday?: number | null
}

type CampaignContentDTO = {
  link?: string | null
  link_type?: string | null
  reward_type?: string | null
  reward_amount?: number | null
  notification_title?: string | null
  notification_content?: string | null
}

type CampaignDetailDTO = {
  id: number
  title?: string | null
  campaign_type?: string | null
  content?: CampaignContentDTO | null
  target_audience?: string | null
  audience_conditions?: AudienceNode | null
  type?: string | null
  // backward compatible (older API)
  schedule_type?: string | null
  schedule_config?: CampaignSchedule | null
}

function pad2(n: number) {
  return String(n).padStart(2, '0')
}

function deriveRecurringType(
  schedule?: CampaignSchedule | null,
): 'daily' | 'weekly' | 'monthly' | 'yearly' {
  const month = Number(schedule?.month || 0)
  const date = Number(schedule?.date || 0)
  const weekday = Number(schedule?.weekday || 0)
  if (month > 0 && date > 0) return 'yearly'
  if (date > 0) return 'monthly'
  if (weekday > 0) return 'weekly'
  return 'daily'
}

function applyDetailToForm(detail: CampaignDetailDTO) {
  const schedule = detail.schedule_config ?? {}
  const content = detail.content ?? {}
  const apiType = String(detail.type ?? detail.schedule_type ?? 'now')
    .trim()
    .toLowerCase()
  const planType: 'now' | 'schedule' | 'future' =
    apiType === 'future' ? 'future' : apiType === 'schedule' ? 'schedule' : 'now'

  // For <input type="date"> we need YYYY-MM-DD. API payload only has month+date, so we assume current year.
  const nowYear = new Date().getFullYear()
  const scheduledMonth = Number(schedule.month || 0)
  const scheduledDate = Number(schedule.date || 0)
  const scheduledDateStr =
    scheduledMonth > 0 && scheduledDate > 0
      ? `${nowYear}-${pad2(scheduledMonth)}-${pad2(scheduledDate)}`
      : ''

  const recurringType = deriveRecurringType(schedule)

  form.value = {
    ...form.value,
    campaign_type:
      detail.campaign_type === PushCampaignTypeEnum.NotificationReward
        ? PushCampaignTypeEnum.NotificationReward
        : PushCampaignTypeEnum.NotificationLink,
    title: detail.title ?? '',
    notification_title: content.notification_title ?? '',
    notification_content: content.notification_content ?? '',
    link_type: content.link_type ?? 'custom',
    link: content.link ?? '',
    reward_type: ((content.reward_type as 'point') ?? 'point') as 'point',
    reward_amount: String(content.reward_amount ?? 0),
    target_audience: detail.target_audience ?? 'all',
    audience_conditions: isAudienceNode(detail.audience_conditions)
      ? detail.audience_conditions
      : ({ type: 'GROUP_OR', child: [] } as AudienceNode),
    type: planType,
    scheduled_date: scheduledDateStr,
    scheduled_hour: pad2(Number(schedule.hour || 8)),
    scheduled_minute: pad2(Number(schedule.minute || 0)),
    recurring_type: recurringType,
    recurring_hour: pad2(Number(schedule.hour || 8)),
    recurring_minute: pad2(Number(schedule.minute || 0)),
    recurring_weekday: String(Number(schedule.weekday || 2)),
    recurring_day: pad2(Number(schedule.date || 1)),
    recurring_month: pad2(Number(schedule.month || 1)),
  }
}

async function fetchDetail(id: number) {
  const res = (await APIClient.get(
    PushCampaignAPI.campaign.detail.replace(':id', String(id)),
  )) as unknown as { data?: unknown }

  const payload = res?.data

  const get = (obj: unknown, keys: string[]): unknown => {
    let cur: unknown = obj
    for (const k of keys) {
      if (!cur || typeof cur !== 'object') return undefined
      const rec = cur as Record<string, unknown>
      cur = rec[k]
    }
    return cur
  }

  // Support common response shapes:
  // - { data: { data: <dto> } }
  // - { data: <dto> }
  // - <dto>
  const dto = (get(payload, ['data', 'data']) ??
    get(payload, ['data']) ??
    payload) as CampaignDetailDTO

  if (dto && typeof dto === 'object') applyDetailToForm(dto)
}

onMounted(async () => {
  if (props.campaignId) {
    try {
      await fetchDetail(props.campaignId)
    } catch (error) {
      console.error('[push-campaign] fetch detail error', error)
      toast.add({
        severity: 'error',
        summary: 'Lỗi',
        detail: 'Không tải được chi tiết chiến dịch',
        life: 3000,
      })
    }
  }
})

watch(
  () => props.campaignId,
  async (id) => {
    if (!id) return
    try {
      await fetchDetail(id)
    } catch (error) {
      console.error('[push-campaign] fetch detail error', error)
    }
  },
)

// Reset hour/minute if currently selected value is no longer in filtered options
watch(isToday, (today) => {
  if (!today) return
  const firstAvailableHour = filteredFutureHourOptions.value[0]
  if (!firstAvailableHour) {
    // All hours have passed today, reset to next available day suggestion
    form.value.scheduled_date = ''
    return
  }
  if (parseInt(form.value.scheduled_hour) <= nowHour) {
    form.value.scheduled_hour = firstAvailableHour.value
    form.value.scheduled_minute = '00'
  }
})

// Reset minute if selected hour equals current hour and selected minute is no longer available
watch(
  () => form.value.scheduled_hour,
  (hour) => {
    if (!isToday.value) return
    const h = parseInt(hour)
    if (h > nowHour) {
      // All minutes are available for future hours
      return
    }
    // h === nowHour - reset minute to first available
    const firstAvailMinute = filteredFutureMinuteOptions.value[0]
    if (!firstAvailMinute) {
      // No minutes available for this hour, reset to 00
      form.value.scheduled_minute = '00'
    } else if (parseInt(form.value.scheduled_minute) <= nowMinute) {
      form.value.scheduled_minute = firstAvailMinute.value
    }
  },
)

function isAudienceNode(value: unknown): value is AudienceNode {
  if (!value || typeof value !== 'object') return false
  if (!('type' in value) || !('child' in value)) return false
  const t = (value as { type?: unknown }).type
  return t === 'GROUP_AND' || t === 'GROUP_OR' || t === 'CONDITION'
}

const buildSchedulePayload = () => {
  const f = form.value

  if (f.type === 'now') {
    return {
      type: 'now',
      schedule_config: {
        hour: 0,
        minute: 0,
        weekday: 0,
        date: 0,
        month: 0,
      } satisfies CampaignSchedule,
    }
  }

  if (f.type === 'future') {
    const [, m, d] = f.scheduled_date.split('-')
    return {
      type: 'future',
      schedule_config: {
        hour: Number(f.scheduled_hour),
        minute: Number(f.scheduled_minute),
        weekday: 0,
        date: Number(d),
        month: Number(m),
      } satisfies CampaignSchedule,
    }
  }

  // schedule (recurring)
  const base = {
    hour: Number(f.recurring_hour),
    minute: Number(f.recurring_minute),
    weekday: 0,
    date: 0,
    month: 0,
  }

  if (f.recurring_type === 'daily') {
    // chỉ cần hour + minute
  } else if (f.recurring_type === 'weekly') {
    base.weekday = Number(f.recurring_weekday)
  } else if (f.recurring_type === 'monthly') {
    base.date = Number(f.recurring_day)
  } else if (f.recurring_type === 'yearly') {
    base.date = Number(f.recurring_day)
    base.month = Number(f.recurring_month)
  }

  return {
    type: 'schedule',
    schedule_config: base satisfies CampaignSchedule,
  }
}

const handleSave = async () => {
  errors.value = validateCampaignForm(form.value)
  if (Object.keys(errors.value).length > 0) {
    const firstError = Object.values(errors.value).find(Boolean)
    toast.add({
      severity: 'warn',
      summary: 'Cảnh báo',
      detail: firstError || 'Vui lòng kiểm tra lại thông tin',
      life: 3000,
    })
    return
  }

  const schedulePayload = buildSchedulePayload()

  const audience_conditions = isAudienceNode(form.value.audience_conditions)
    ? form.value.audience_conditions
    : ({ type: 'GROUP_OR', child: [] } as AudienceNode)

  const payload = {
    campaign_type: form.value.campaign_type,
    title: form.value.title,
    content: {
      notification_title: form.value.notification_title,
      notification_content: form.value.notification_content,
      link_type: form.value.link_type,
      link: form.value.link,
      reward_type: form.value.reward_type,
      reward_amount: Number(form.value.reward_amount) || 0,
    },
    target_audience: form.value.target_audience,
    audience_conditions,
    ...schedulePayload,
  }

  console.log('[push-campaign] payload', payload)

  try {
    if (props.campaignId) {
      await APIClient.put(
        PushCampaignAPI.campaign.update.replace(':id', String(props.campaignId)),
        payload,
      )
    } else {
      await APIClient.post(PushCampaignAPI.campaign.create, payload)
    }
    toast.add({
      severity: 'success',
      summary: 'Thành công',
      detail: 'Lưu chiến dịch thành công',
      life: 3000,
    })
    setTimeout(() => {
      router.push('/dashboard/push-campaign/list')
    }, 500)
  } catch (error) {
    console.error('[push-campaign] save error', error)
    toast.add({
      severity: 'error',
      summary: 'Lỗi',
      detail: 'Lưu chiến dịch thất bại',
      life: 3000,
    })
  }
}
</script>

<template>
  <div class="flex gap-4 p-4">
    <!-- Left panel: campaign type -->
    <div class="shrink-0">
      <div class="campaign-card flex flex-col gap-4 p-4">
        <p class="font-bold text-[16px] text-black">
          Chọn loại chiến dịch <span class="text-[red]">*</span>
        </p>
        <label
          class="flex items-center gap-[10px] p-7 rounded-lg cursor-pointer border"
          :class="
            form.campaign_type === PushCampaignTypeEnum.NotificationLink
              ? 'border-[#0095ff] bg-[#e9f5ff]'
              : 'border-[#e2e2e2] bg-white'
          "
        >
          <input
            type="radio"
            name="campaign_type"
            :value="PushCampaignTypeEnum.NotificationLink"
            v-model="form.campaign_type"
            class="accent-[#0095ff] w-4 h-4"
          />
          <span class="font-bold text-[16px] text-black">Thông báo + liên kết</span>
        </label>
        <label
          class="flex items-center gap-[10px] p-7 rounded-lg cursor-pointer border"
          :class="
            form.campaign_type === PushCampaignTypeEnum.NotificationReward
              ? 'border-[#0095ff] bg-[#e9f5ff]'
              : 'border-[#e2e2e2] bg-white'
          "
        >
          <input
            type="radio"
            name="campaign_type"
            :value="PushCampaignTypeEnum.NotificationReward"
            v-model="form.campaign_type"
            class="accent-[#0095ff] w-4 h-4"
          />
          <span class="font-bold text-[16px] text-black">Thông báo + điểm thưởng</span>
        </label>
      </div>
    </div>

    <!-- Right panel: form sections -->
    <div class="flex-1 flex flex-col gap-4">
      <!-- Tiêu đề chiến dịch -->
      <div class="campaign-card flex flex-col gap-[10px] p-3">
        <p class="font-bold text-[16px] text-black">
          Tiêu đề chiến dịch <span class="text-[red]">*</span>
        </p>
        <InputText
          v-model="form.title"
          placeholder="Nhập tiêu đề chiến dịch"
          class="w-full campaign-input"
          :class="{ 'p-invalid': errors.title }"
        />
        <small v-if="errors.title" class="text-[#e03e3e] text-[12px]">{{ errors.title }}</small>
      </div>

      <!-- Tiêu đề thông báo + Nội dung + Liên kết -->
      <div class="campaign-card flex flex-col gap-[10px] p-3">
        <p class="font-bold text-[16px] text-black">
          Tiêu đề thông báo <span class="text-[red]">*</span>
        </p>
        <InputText
          v-model="form.notification_title"
          placeholder="Nhập tiêu đề thông báo"
          class="w-full campaign-input"
          :class="{ 'p-invalid': errors.notification_title }"
        />
        <small v-if="errors.notification_title" class="text-[#e03e3e] text-[12px]">{{ errors.notification_title }}</small>

        <div class="h-[10px]" />

        <p class="font-bold text-[16px] text-black">
          Nội dung thông báo <span class="text-[red]">*</span>
        </p>
        <Textarea
          v-model="form.notification_content"
          placeholder="Nhập nội dung thông báo"
          rows="4"
          class="w-full campaign-input"
          :class="{ 'p-invalid': errors.notification_content }"
        />
        <small v-if="errors.notification_content" class="text-[#e03e3e] text-[12px]">{{ errors.notification_content }}</small>

        <div class="h-[10px]" />

        <template v-if="form.campaign_type === PushCampaignTypeEnum.NotificationLink">
          <p class="font-bold text-[16px] text-black">Liên kết <span class="text-[red]">*</span></p>
          <div class="flex gap-4 items-center">
            <Select
              v-model="form.link_type"
              :options="linkTypeOptions"
              optionLabel="label"
              optionValue="value"
              placeholder="Chọn loại liên kết"
              class="w-[179px] shrink-0 campaign-input"
              :class="{ 'p-invalid': errors.link_type }"
            />
            <InputText
              v-model="form.link"
              placeholder="Nhập liên kết tùy chỉnh"
              class="flex-1 campaign-input"
              :class="{ 'p-invalid': errors.link }"
            />
          </div>
          <small v-if="errors.link" class="text-[#e03e3e] text-[12px]">{{ errors.link }}</small>
        </template>

        <template v-if="form.campaign_type === PushCampaignTypeEnum.NotificationReward">
          <div class="h-[10px]" />
          <p class="font-bold text-[16px] text-black">
            Số điểm thưởng <span class="text-[red]">*</span>
          </p>
          <InputText
            v-model="form.reward_amount"
            type="number"
            min="0"
            placeholder="Nhập số điểm thưởng"
            class="w-full campaign-input"
            :class="{ 'p-invalid': errors.reward_amount }"
          />
          <small v-if="errors.reward_amount" class="text-[#e03e3e] text-[12px]">{{ errors.reward_amount }}</small>
        </template>
      </div>

      <!-- Đối tượng áp dụng -->
      <div class="campaign-card flex flex-col gap-[10px] p-3">
        <p class="font-bold text-[16px] text-black">
          Đối tượng áp dụng <span class="text-[red]">*</span>
        </p>
        <ConditionBuilder v-model="form.audience_conditions" />
      </div>

      <!-- Kế hoạch gửi -->
      <div class="campaign-card flex flex-col gap-[10px] p-3">
        <p class="font-bold text-[16px] text-black">
          Kế hoạch gửi <span class="text-[red]">*</span>
        </p>

        <div class="h-[10px]" />

        <!-- Hình thức gửi -->
        <div class="flex items-center gap-4">
          <span class="font-bold text-[12px] text-black shrink-0">Hình thức gửi</span>
          <label class="flex items-center gap-[7px] cursor-pointer">
            <input
              type="radio"
              v-model="form.type"
              value="now"
              class="accent-[#0095ff] w-[14px] h-[14px]"
            />
            <span class="text-[12px] text-black">Gửi ngay lập tức</span>
          </label>
          <label class="flex items-center gap-[7px] cursor-pointer">
            <input
              type="radio"
              v-model="form.type"
              value="schedule"
              class="accent-[#0095ff] w-[14px] h-[14px]"
            />
            <span class="text-[12px] text-black">Gửi định kỳ</span>
          </label>
          <label class="flex items-center gap-[7px] cursor-pointer">
            <input
              type="radio"
              v-model="form.type"
              value="future"
              class="accent-[#0095ff] w-[14px] h-[14px]"
            />
            <span class="text-[12px] text-black">Lập lịch để gửi</span>
          </label>
        </div>

        <div class="h-[10px]" />

        <!-- Trạng thái: Lập lịch để gửi -->
        <div v-if="form.type === 'future'" class="bg-[#fafafa] flex items-center gap-5 p-4">
          <span class="font-bold text-[12px] text-black shrink-0"
            >Chọn ngày gửi trong tương lai</span
          >
          <div class="bg-white flex items-center gap-[10px] px-4 py-2 rounded" :class="{ 'ring-1 ring-[#e03e3e]': errors.scheduled_date }">
            <input
              type="date"
              v-model="form.scheduled_date"
              :min="minDate"
              class="text-[13px] text-black border-none outline-none bg-transparent cursor-pointer"
            />
          </div>
          <small v-if="errors.scheduled_date" class="text-[#e03e3e] text-[12px]">{{ errors.scheduled_date }}</small>
          <span class="text-[12px] text-black">vào lúc</span>
          <div class="flex items-center gap-[7px]">
            <Select
              v-model="form.scheduled_hour"
              :options="filteredFutureHourOptions"
              optionLabel="label"
              optionValue="value"
              class="schedule-select"
            />
            <span class="text-[16px] text-black">:</span>
            <Select
              v-model="form.scheduled_minute"
              :options="filteredFutureMinuteOptions"
              optionLabel="label"
              optionValue="value"
              class="schedule-select"
            />
          </div>
        </div>

        <!-- Trạng thái: Gửi định kỳ -->
        <div v-if="form.type === 'schedule'" class="bg-[#fafafa] flex items-start gap-5 p-4">
          <span class="font-bold text-[12px] text-black shrink-0 mt-[10px]"
            >Cài đặt gửi định kỳ</span
          >
          <div class="flex flex-col gap-[22px]">
            <!-- Hàng ngày -->
            <div class="flex items-center gap-4">
              <label class="flex items-center gap-[7px] cursor-pointer shrink-0">
                <input
                  type="radio"
                  v-model="form.recurring_type"
                  value="daily"
                  class="accent-[#0095ff] w-[14px] h-[14px]"
                />
                <span class="text-[12px] text-black">Gửi hàng ngày vào lúc</span>
              </label>
              <div class="flex items-center gap-[7px]">
                <Select
                  v-model="form.recurring_hour"
                  :options="hourOptions"
                  optionLabel="label"
                  optionValue="value"
                  class="schedule-select"
                />
                <span class="text-[16px] text-black">:</span>
                <Select
                  v-model="form.recurring_minute"
                  :options="minuteOptions"
                  optionLabel="label"
                  optionValue="value"
                  class="schedule-select"
                />
              </div>
            </div>

            <!-- Hàng tuần -->
            <div class="flex items-center gap-4">
              <label class="flex items-center gap-[7px] cursor-pointer shrink-0">
                <input
                  type="radio"
                  v-model="form.recurring_type"
                  value="weekly"
                  class="accent-[#0095ff] w-[14px] h-[14px]"
                />
                <span class="text-[12px] text-black">Gửi hàng tuần mỗi ngày</span>
              </label>
              <Select
                v-model="form.recurring_weekday"
                :options="weekdayOptions"
                optionLabel="label"
                optionValue="value"
                class="schedule-select"
              />
              <span class="text-[12px] text-black">vào lúc</span>
              <div class="flex items-center gap-[7px]">
                <Select
                  v-model="form.recurring_hour"
                  :options="hourOptions"
                  optionLabel="label"
                  optionValue="value"
                  class="schedule-select"
                />
                <span class="text-[16px] text-black">:</span>
                <Select
                  v-model="form.recurring_minute"
                  :options="minuteOptions"
                  optionLabel="label"
                  optionValue="value"
                  class="schedule-select"
                />
              </div>
            </div>

            <!-- Hàng tháng -->
            <div class="flex items-center gap-4">
              <label class="flex items-center gap-[7px] cursor-pointer shrink-0">
                <input
                  type="radio"
                  v-model="form.recurring_type"
                  value="monthly"
                  class="accent-[#0095ff] w-[14px] h-[14px]"
                />
                <span class="text-[12px] text-black">Gửi hàng tháng vào ngày</span>
              </label>
              <Select
                v-model="form.recurring_day"
                :options="dayOptions"
                optionLabel="label"
                optionValue="value"
                class="schedule-select"
              />
              <span class="text-[12px] text-black">vào lúc</span>
              <div class="flex items-center gap-[7px]">
                <Select
                  v-model="form.recurring_hour"
                  :options="hourOptions"
                  optionLabel="label"
                  optionValue="value"
                  class="schedule-select"
                />
                <span class="text-[16px] text-black">:</span>
                <Select
                  v-model="form.recurring_minute"
                  :options="minuteOptions"
                  optionLabel="label"
                  optionValue="value"
                  class="schedule-select"
                />
              </div>
            </div>

            <!-- Hàng năm -->
            <div class="flex items-center gap-4">
              <label class="flex items-center gap-[7px] cursor-pointer shrink-0">
                <input
                  type="radio"
                  v-model="form.recurring_type"
                  value="yearly"
                  class="accent-[#0095ff] w-[14px] h-[14px]"
                />
                <span class="text-[12px] text-black">Gửi hàng năm vào ngày</span>
              </label>
              <div class="flex items-center gap-[7px]">
                <Select
                  v-model="form.recurring_day"
                  :options="dayOptions"
                  optionLabel="label"
                  optionValue="value"
                  class="schedule-select"
                />
                <span class="text-[16px] text-black">/</span>
                <Select
                  v-model="form.recurring_month"
                  :options="monthOptions"
                  optionLabel="label"
                  optionValue="value"
                  class="schedule-select"
                />
              </div>
              <span class="text-[12px] text-black">vào lúc</span>
              <div class="flex items-center gap-[7px]">
                <Select
                  v-model="form.recurring_hour"
                  :options="hourOptions"
                  optionLabel="label"
                  optionValue="value"
                  class="schedule-select"
                />
                <span class="text-[16px] text-black">:</span>
                <Select
                  v-model="form.recurring_minute"
                  :options="minuteOptions"
                  optionLabel="label"
                  optionValue="value"
                  class="schedule-select"
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Save button -->
      <div class="campaign-card flex items-center justify-end p-4">
        <button
          class="flex items-center gap-[10px] bg-[#008724] hover:bg-[#006e1d] text-white font-bold text-[10px] px-4 py-[7px] rounded-lg cursor-pointer"
          @click="handleSave"
        >
          <Icon icon="mingcute:save-fill" height="24px" />
          Lưu chiến dịch
        </button>
      </div>
    </div>
  </div>
</template>

<style scoped>
.campaign-card {
  background: white;
  border-radius: 8px;
  box-shadow: 0px 0px 4px 0px rgba(0, 0, 0, 0.14);
  overflow: clip;
}

:deep(.campaign-input.p-inputtext),
:deep(.campaign-input .p-inputtext),
:deep(.campaign-input.p-textarea),
:deep(.campaign-input .p-select-label) {
  background: #f7f7f7;
  border: none;
  font-size: 16px;
  padding: 16px;
  color: #333;
}

:deep(.campaign-input.p-textarea) {
  min-height: 129px;
}

:deep(.schedule-select.p-select) {
  background: white;
  border: none;
  border-radius: 4px;
  min-width: unset;
  height: 40px;
}

:deep(.schedule-select .p-select-label) {
  background: white;
  border: none;
  font-size: 13px;
  padding: 0 16px;
  line-height: 40px;
}

:deep(.schedule-select .p-select-dropdown) {
  width: auto;
  padding-right: 16px;
}

:deep(.campaign-input.p-select),
:deep(.campaign-input.p-select .p-select-label) {
  background: #f7f7f7;
  border: none;
  font-size: 16px;
}

:deep(.campaign-input.p-inputtext::placeholder),
:deep(.campaign-input .p-inputtext::placeholder),
:deep(.campaign-input.p-textarea::placeholder),
:deep(.campaign-input .p-select-label.p-placeholder) {
  color: #777;
  font-size: 16px;
}

:deep(.campaign-input.p-invalid),
:deep(.campaign-input .p-inputtext.p-invalid) {
  border: 1px solid #e03e3e !important;
  border-radius: 4px;
}

:deep(.p-select.p-invalid) {
  border: 1px solid #e03e3e !important;
  border-radius: 4px;
}
</style>
